import { useEffect, useId, useRef } from 'react';
import { Link } from 'react-router-dom';

/**
 * Aksi berbentuk ikon dengan keterangan yang muncul saat disentuh penunjuk —
 * dipakai di kartu `/cameras` (§G11) dan di bilah dashboard (§G8.2).
 *
 * Ikonnya memakai atribut yang sama persis dengan ikon navigasi (§G9): 20px,
 * stroke 1,75, ujung membulat. Satu halaman tidak boleh punya dua bahasa ikon.
 */
const ikon = {
  width: 20,
  height: 20,
  viewBox: '0 0 24 24',
  fill: 'none',
  stroke: 'currentColor',
  strokeWidth: 1.75,
  strokeLinecap: 'round' as const,
  strokeLinejoin: 'round' as const,
  'aria-hidden': true,
};

export const IKON_AKSI = {
  /** Live view — badan kamera foto, sama seperti tombol layar penuh di tile. */
  live: (
    <svg {...ikon}>
      <path d="M4 8.5h2.6l1.3-2h8.2l1.3 2H20a1.5 1.5 0 0 1 1.5 1.5v7A1.5 1.5 0 0 1 20 18.5H4A1.5 1.5 0 0 1 2.5 17v-7A1.5 1.5 0 0 1 4 8.5Z" />
      <circle cx="12" cy="13.25" r="3" />
    </svg>
  ),
  /** Playback — segitiga di dalam lingkaran, sama dengan tombol putar §G10.1. */
  playback: (
    <svg {...ikon}>
      <circle cx="12" cy="12" r="9" />
      {/* Digeser 0,4px ke kanan: segitiga yang dipusatkan secara geometris
          selalu terlihat terlalu ke kiri di dalam lingkaran (§G10.1). */}
      <path d="m10.4 8.6 5.2 3.4-5.2 3.4z" />
    </svg>
  ),
  /** View logs — lembar bergaris. */
  logs: (
    <svg {...ikon}>
      <path d="M14 3H7a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h10a2 2 0 0 0 2-2V8z" />
      <path d="M14 3v5h5M9 13h6M9 17h4" />
    </svg>
  ),
  /** Test stream — kilat. Uji koneksi, bukan menonton. */
  test: (
    <svg {...ikon}>
      <path d="M13 2 4.5 13.5H11l-1 8.5 8.5-11.5H12z" />
    </svg>
  ),
  /** More options — tiga titik tegak. */
  more: (
    <svg {...ikon}>
      <circle cx="12" cy="5.5" r="1.1" fill="currentColor" stroke="none" />
      <circle cx="12" cy="12" r="1.1" fill="currentColor" stroke="none" />
      <circle cx="12" cy="18.5" r="1.1" fill="currentColor" stroke="none" />
    </svg>
  ),
  edit: (
    <svg {...ikon}>
      <path d="M4 20h4L19.5 8.5a2.1 2.1 0 0 0-3-3L5 17v3z" />
      <path d="m14.5 5.5 4 4" />
    </svg>
  ),
  gear: (
    <svg {...ikon}>
      <circle cx="12" cy="12" r="3.25" />
      <path d="M12 2.75v2.4M12 18.85v2.4M21.25 12h-2.4M5.15 12h-2.4M18.54 5.46l-1.7 1.7M7.16 16.84l-1.7 1.7M18.54 18.54l-1.7-1.7M7.16 7.16l-1.7-1.7" />
      <circle cx="12" cy="12" r="8" />
    </svg>
  ),
  trash: (
    <svg {...ikon}>
      <path d="M4 7h16M10 7V5a1 1 0 0 1 1-1h2a1 1 0 0 1 1 1v2" />
      <path d="M6 7l1 12a2 2 0 0 0 2 2h6a2 2 0 0 0 2-2l1-12" />
      <path d="M10 11v6M14 11v6" />
    </svg>
  ),
  /**
   * Add to dashboard — lembaran bertumpuk dengan tanda tambah, dipinjam dari
   * ikon `layers` milik menu Custom Dashboards (§G9) supaya keduanya terbaca
   * sebagai barang yang sama.
   */
  addToDashboard: (
    <svg {...ikon}>
      <path d="m12 3 9 5-9 5-9-5z" />
      <path d="m3 13 6.5 3.6" />
      <path d="M18 15v6M15 18h6" />
    </svg>
  ),
  /**
   * Fullscreen — empat sudut yang menarik diri ke luar. Dipakai di bilah
   * dashboard (§G8.2) untuk mode layar penuh manual.
   */
  fullscreen: (
    <svg {...ikon}>
      <path d="M8 3H5a2 2 0 0 0-2 2v3" />
      <path d="M16 3h3a2 2 0 0 1 2 2v3" />
      <path d="M8 21H5a2 2 0 0 1-2-2v-3" />
      <path d="M16 21h3a2 2 0 0 0 2-2v-3" />
    </svg>
  ),
  /**
   * Slideshow — bingkai layar dengan segitiga putar di dalamnya. Bukan
   * segitiga telanjang: yang telanjang sudah dipakai `playback`, dan dua
   * tombol yang artinya berbeda tidak boleh memakai ikon yang sama.
   */
  slideshow: (
    <svg {...ikon}>
      <rect x="2.5" y="4.5" width="19" height="13" rx="2" />
      <path d="M8 21h8" />
      {/* Digeser 0,3px ke kanan, alasan yang sama dengan `playback`. */}
      <path d="m10.3 8.1 4.7 2.9-4.7 2.9z" />
    </svg>
  ),
  /**
   * ——— Bilah Playback (§G10.1, ikon sejak 7 September 2026) ———
   *
   * Enam tombol berlabel kata dulu memakan satu baris penuh di 1280px dan
   * membungkus jadi dua di laptop 13", dan baris kedua itu memakan tinggi
   * gambar — barang yang paling mahal di halaman ini. Ikon memakai bahasa yang
   * sama dengan sisa aplikasi: 20px, stroke 1,75, ujung membulat.
   */

  /** Mark in — kurung siku menghadap kanan, penanda batas KIRI rentang. */
  markIn: (
    <svg {...ikon}>
      <path d="M8 4H5v16h3" />
      <path d="M12 4v16" />
      <path d="M16 12h4M18 10l2 2-2 2" />
    </svg>
  ),
  /** Mark out — cerminnya: batas KANAN rentang. */
  markOut: (
    <svg {...ikon}>
      <path d="M16 4h3v16h-3" />
      <path d="M12 4v16" />
      <path d="M8 12H4M6 10l-2 2 2 2" />
    </svg>
  ),
  /** Clear marks — kedua batas dengan satu coretan. */
  clearMarks: (
    <svg {...ikon}>
      <path d="M6 5v14M18 5v14" />
      <path d="m8.5 15.5 7-7M8.5 8.5l7 7" />
    </svg>
  ),
  /** Clips — bingkai film: daftar berkas, bukan daftar jam. */
  clips: (
    <svg {...ikon}>
      <rect x="3" y="5" width="18" height="14" rx="2" />
      <path d="M8 5v14M16 5v14M3 12h18" />
    </svg>
  ),
  /** Bookmarks — pita penanda. */
  bookmark: (
    <svg {...ikon}>
      <path d="M6.5 3.5h11a1 1 0 0 1 1 1v16l-6.5-4-6.5 4v-16a1 1 0 0 1 1-1z" />
    </svg>
  ),
  /** Download — panah turun ke nampan, sama dengan ikon menu Downloads (§G9). */
  download: (
    <svg {...ikon}>
      <path d="M12 3v12" />
      <path d="m7.5 10.5 4.5 4.5 4.5-4.5" />
      <path d="M4 17v2a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2v-2" />
    </svg>
  ),
  /** Help — tanda tanya di dalam lingkaran. */
  help: (
    <svg {...ikon}>
      <circle cx="12" cy="12" r="9" />
      <path d="M9.6 9.3a2.5 2.5 0 1 1 3.3 2.4c-.6.2-.9.7-.9 1.3v.5" />
      <circle cx="12" cy="16.75" r="1" fill="currentColor" stroke="none" />
    </svg>
  ),
  /** Gelombang siar — penanda profil stream di dalam menu Test. */
  stream: (
    <svg {...ikon}>
      <path d="M5 20h.01" />
      <path d="M4 14a6 6 0 0 1 6 6" />
      <path d="M4 8a12 12 0 0 1 12 12" />
    </svg>
  ),
} as const;

export type NamaIkon = keyof typeof IKON_AKSI;

/**
 * Ukuran tombol: 36×36.
 *
 * Di atas ambang 24×24 WCAG 2.5.8, di bawah 40px yang biasanya dituju di
 * desktop. Angkanya ditentukan kepadatan kartu: pada ukuran `Small` kartunya
 * ±180px dan lima tombol 40px memaksa barisnya patah jadi dua di setiap kartu.
 * Yang digantikan tombol ini adalah delapan tombol teks yang **selalu** patah
 * jadi dua-tiga baris, jadi 36px tetap perbaikan bersih.
 */
const DASAR =
  'inline-flex h-9 w-9 items-center justify-center rounded-md border border-line text-content transition-[background-color,scale] duration-150 hover:bg-subtle active:scale-[0.98] motion-reduce:transition-none';

/**
 * Keterangan yang muncul di bawah tombol.
 *
 * Ia **duplikat** dari `aria-label` tombolnya, jadi `aria-hidden` — tanpa itu
 * pembaca layar menyebutkan namanya dua kali. Yang membacakan tetap label
 * tombolnya; gelembung ini semata-mata untuk mata.
 *
 * Muncul saat hover **dan** saat fokus papan ketik: tanpa cabang fokus,
 * satu-satunya cara mengetahui tombol ini melakukan apa adalah punya tetikus.
 *
 * `[@media(hover:hover)]` menyingkirkan hover di layar sentuh. Tanpa gerbang
 * itu, satu ketukan di HP meninggalkan gelembung yang menempel sampai ada yang
 * diketuk di tempat lain — dan di kartu yang tombolnya berjajar, gelembung itu
 * menutupi tombol sebelahnya.
 */
function Keterangan({ label }: { label: string }) {
  return (
    <span
      aria-hidden="true"
      className="pointer-events-none absolute left-1/2 top-full z-20 mt-1.5 -translate-x-1/2 whitespace-nowrap rounded-md bg-content px-2 py-1 text-xs text-surface opacity-0 shadow-lg transition-opacity duration-150 group-focus-within:opacity-100 motion-reduce:transition-none [@media(hover:hover)]:group-hover:opacity-100"
    >
      {label}
    </span>
  );
}

interface DasarProps {
  /** Nama aksi. Jadi `aria-label` tombol **dan** isi gelembung keterangan. */
  label: string;
  nama: NamaIkon;
  /** Merah untuk aksi merusak (§G3). */
  merusak?: boolean;
  title?: string;
}

/**
 * Angka kecil di pojok tombol — jumlah klip, jumlah bookmark.
 *
 * `aria-hidden`: angkanya **sudah** ada di dalam `label` tombolnya ("Clips
 * (12)"), dan pembaca layar yang menyebutkannya dua kali membuat tombolnya
 * terdengar seperti dua benda.
 */
function Lencana({ isi }: { isi: string | number }) {
  return (
    <span
      aria-hidden="true"
      className="pointer-events-none absolute -right-1 -top-1 min-w-4 rounded-full bg-accent px-1 text-center text-[10px] font-semibold leading-4 tabular-nums text-accent-contrast"
    >
      {isi}
    </span>
  );
}

export function TombolIkon({
  label,
  nama,
  merusak = false,
  /** Panel yang dibukanya sedang terbuka — bukan sekadar hover. */
  aktif = false,
  /** Tombol aksi utama di barisnya (§G3): satu saja per baris. */
  utama = false,
  /** Angka di pojok, mis. jumlah klip. */
  tanda,
  onClick,
  ...rest
}: DasarProps & {
  aktif?: boolean;
  utama?: boolean;
  tanda?: string | number | null;
  onClick: () => void;
} & React.ButtonHTMLAttributes<HTMLButtonElement>) {
  const gaya = utama
    ? 'border-transparent bg-accent text-accent-contrast hover:brightness-110'
    : merusak
      ? 'border-red-600 text-red-600 hover:bg-red-600/10'
      : aktif
        ? 'border-accent bg-subtle text-accent-text'
        : '';
  return (
    <span className="group relative inline-flex">
      <button
        type="button"
        onClick={onClick}
        aria-label={label}
        className={`${DASAR} ${gaya} disabled:pointer-events-none disabled:opacity-40`}
        {...rest}
      >
        {IKON_AKSI[nama]}
      </button>
      {tanda !== null && tanda !== undefined && <Lencana isi={tanda} />}
      <Keterangan label={label} />
    </span>
  );
}

/** Versi tautan — `<a>` sungguhan, supaya Cmd/klik-tengah tetap membuka tab. */
export function TautanIkon({
  label,
  nama,
  to,
  luar = false,
  ...rest
}: DasarProps & { to: string; luar?: boolean }) {
  const isi = IKON_AKSI[nama];
  return (
    <span className="group relative inline-flex">
      {luar ? (
        <a href={to} target="_blank" rel="noreferrer noopener" aria-label={label} className={DASAR} {...rest}>
          {isi}
        </a>
      ) : (
        <Link to={to} aria-label={label} className={DASAR} {...rest}>
          {isi}
        </Link>
      )}
      <Keterangan label={label} />
    </span>
  );
}

/** Kelas satu baris di dalam panel — dipakai pemanggil untuk isinya. */
export const ITEM_MENU =
  'flex min-h-tap w-full items-center gap-2.5 rounded-md px-2.5 text-left text-sm hover:bg-subtle';

/**
 * Tombol ikon yang membuka panel kecil di bawahnya.
 *
 * **Bukan `role="menu"`.** Panel ini berisi tombol dan tautan biasa, dan
 * `role="menu"` mewajibkan seluruh paket APG ikut dipasang — panah naik-turun,
 * roving tabindex, Home/End — yang kalau setengah jadi justru **mencabut** Tab
 * yang tadinya bekerja. Sebagai pengungkap biasa, isinya berada tepat sesudah
 * tombolnya di DOM sehingga Tab sudah menjelajahinya dengan benar tanpa satu
 * baris JavaScript pun. Yang perlu ditambahkan tinggal dua hal yang memang
 * tidak gratis: Escape menutup, dan fokus kembali ke tombol pembukanya.
 *
 * Keadaan buka/tutup dipegang pemanggil, bukan di sini: pada 96 kartu, panel
 * yang menyimpan keadaannya sendiri berarti 96 panel yang bisa terbuka
 * bersamaan, dan menutup yang satu saat yang lain dibuka jadi mustahil.
 */
export function MenuIkon({
  label,
  nama,
  terbuka,
  onToggle,
  onTutup,
  children,
}: DasarProps & {
  terbuka: boolean;
  onToggle: () => void;
  onTutup: () => void;
  children: React.ReactNode;
}) {
  const id = useId();
  const bungkusRef = useRef<HTMLSpanElement>(null);
  const tombolRef = useRef<HTMLButtonElement>(null);
  const panelRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!terbuka) return;

    const onKey = (e: KeyboardEvent) => {
      if (e.key !== 'Escape') return;
      // Dihentikan di sini supaya Escape yang dimaksudkan menutup panel tidak
      // ikut menutup layar penuh atau dialog di belakangnya (§G8.2).
      e.stopPropagation();
      onTutup();
      tombolRef.current?.focus();
    };

    // `pointerdown`, bukan `click`: menutup panel harus terjadi saat tombol
    // ditekan, bukan saat dilepas. Dengan `click`, menekan tombol lain sambil
    // panel terbuka menghasilkan satu ketukan yang habis hanya untuk menutup.
    const onTekan = (e: PointerEvent) => {
      if (!bungkusRef.current?.contains(e.target as Node)) onTutup();
    };

    document.addEventListener('keydown', onKey, true);
    document.addEventListener('pointerdown', onTekan);
    return () => {
      document.removeEventListener('keydown', onKey, true);
      document.removeEventListener('pointerdown', onTekan);
    };
  }, [terbuka, onTutup]);

  // Fokus pindah ke baris pertama saat panel terbuka. Tanpa ini, orang yang
  // membuka panel dengan papan ketik harus menekan Tab sekali lagi untuk masuk
  // ke isinya — dan tidak ada apa pun di layar yang memberi tahu itu.
  useEffect(() => {
    if (terbuka) panelRef.current?.querySelector<HTMLElement>('[data-item]')?.focus();
  }, [terbuka]);

  return (
    <span ref={bungkusRef} className="group relative inline-flex">
      <button
        ref={tombolRef}
        type="button"
        onClick={onToggle}
        aria-label={label}
        aria-expanded={terbuka}
        aria-controls={terbuka ? id : undefined}
        className={`${DASAR} ${terbuka ? 'bg-subtle' : ''}`}
      >
        {IKON_AKSI[nama]}
      </button>
      {!terbuka && <Keterangan label={label} />}

      {terbuka && (
        <div
          ref={panelRef}
          id={id}
          role="group"
          aria-label={label}
          // `right-0`: panel tumbuh ke kiri dari tombol paling kanan kartu.
          // Tumbuh ke kanan akan keluar dari tepi layar pada kartu di kolom
          // terakhir, dan tidak ada yang bisa menggulung ke sana.
          className="absolute right-0 top-full z-30 mt-1.5 min-w-[13rem] rounded-lg border border-line bg-surface p-1 shadow-xl"
        >
          {children}
        </div>
      )}
    </span>
  );
}
